import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { LeftOutlined, RightOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from './api';
import MemberTabs from './MemberTabs';
import AvailabilityGrid from './AvailabilityGrid';
import CommonSlotsPanel from './CommonSlotsPanel';
import ConfirmedBanner from './ConfirmedBanner';
import { allSlotTimes, formatDateKey, slotKey, weekDays, weekStartOf } from './weekUtil';

export default function LostArkSchedulePage() {
  const [weekStartDate, setWeekStartDate] = useState(() => weekStartOf(dayjs()));
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  // { [memberId]: { [dateTimeKey]: available } } - 저장 버튼을 누르기 전까지의 로컬 변경분
  const [pendingByMember, setPendingByMember] = useState({});
  const commonSlotsRef = useRef(null);

  const times = useMemo(() => allSlotTimes(), []);
  const days = useMemo(() => weekDays(weekStartDate), [weekStartDate]);

  const fetchWeek = useCallback(async (weekStart) => {
    setLoading(true);
    try {
      const res = await api.get('/week', { params: { week_start_date: formatDateKey(weekStart) } });
      setSchedule(res.data);
      setSelectedMemberId((prev) => prev ?? res.data.members[0]?.member_id);
    } catch (e) {
      message.error('시간표를 불러오지 못했어요');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWeek(weekStartDate); }, [weekStartDate, fetchWeek]);

  const selectedMember = useMemo(
    () => schedule?.members.find((m) => m.member_id === selectedMemberId),
    [schedule, selectedMemberId]
  );

  const savedSlotsOf = useCallback((memberId) => {
    const member = schedule?.members.find((m) => m.member_id === memberId);
    const set = new Set();
    (member?.slots || []).forEach((s) => set.add(slotKey(s.date, s.time)));
    return set;
  }, [schedule]);

  const effectiveSlots = useMemo(() => {
    const set = savedSlotsOf(selectedMemberId);
    const pending = pendingByMember[selectedMemberId] || {};
    Object.entries(pending).forEach(([key, available]) => {
      if (available) set.add(key); else set.delete(key);
    });
    return set;
  }, [savedSlotsOf, selectedMemberId, pendingByMember]);

  const pendingCount = Object.keys(pendingByMember[selectedMemberId] || {}).length;
  const hasAnyPending = Object.values(pendingByMember).some((m) => Object.keys(m).length > 0);

  const handleToggle = (dateStr, timeStr, available) => {
    const key = slotKey(dateStr, timeStr);
    const savedAvailable = savedSlotsOf(selectedMemberId).has(key);
    setPendingByMember((prev) => {
      const memberPending = { ...(prev[selectedMemberId] || {}) };
      if (available === savedAvailable) {
        delete memberPending[key];
      } else {
        memberPending[key] = available;
      }
      return { ...prev, [selectedMemberId]: memberPending };
    });
  };

  const handleSaveSlots = async () => {
    const memberPending = pendingByMember[selectedMemberId] || {};
    const entries = Object.entries(memberPending);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      const changes = entries.map(([key, available]) => {
        const [date, time] = key.split('|');
        return { date, time: `${time}:00`, available };
      });
      const res = await api.put('/slots', { member_id: selectedMemberId, changes });
      setSchedule(res.data);
      setPendingByMember((prev) => {
        const next = { ...prev };
        delete next[selectedMemberId];
        return next;
      });
      message.success(`${entries.length}건 저장했어요`);
    } catch (e) {
      message.error('저장에 실패했어요');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (submitted) => {
    setSubmitting(true);
    try {
      const res = await api.patch(`/members/${selectedMemberId}/submit`, {
        week_start_date: formatDateKey(weekStartDate),
        submitted,
      });
      setSchedule(res.data);
      message.success(submitted ? '마감했어요' : '마감을 취소했어요');
    } catch (e) {
      message.error(submitted ? '1개 이상 슬롯을 선택해주세요' : '마감 취소에 실패했어요');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (date, time) => {
    try {
      const res = await api.post('/confirm', {
        week_start_date: formatDateKey(weekStartDate),
        date,
        time,
      });
      setSchedule(res.data);
      message.success('레이드 시각을 확정했어요');
    } catch (e) {
      message.error('확정에 실패했어요');
    }
  };

  const handleCancelConfirm = async () => {
    try {
      const res = await api.delete('/confirm', { params: { week_start_date: formatDateKey(weekStartDate) } });
      setSchedule(res.data);
      message.success('확정을 취소했어요');
    } catch (e) {
      message.error('취소에 실패했어요');
    }
  };

  const goToWeek = (newWeekStart) => {
    const doGo = () => {
      setSelectedMemberId(null);
      setPendingByMember({});
      setWeekStartDate(newWeekStart);
    };

    if (hasAnyPending) {
      Modal.confirm({
        title: '저장하지 않은 변경사항이 있어요',
        content: '주를 이동하면 저장하지 않은 슬롯 변경사항이 사라집니다. 계속할까요?',
        okText: '이동',
        cancelText: '취소',
        onOk: doGo,
      });
      return;
    }
    doGo();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>로스트아크 시간표 조정</Typography.Title>
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => goToWeek(weekStartDate.subtract(7, 'day'))} />
          <Button onClick={() => goToWeek(weekStartOf(dayjs()))}>이번주로</Button>
          <Button icon={<RightOutlined />} onClick={() => goToWeek(weekStartDate.add(7, 'day'))} />
        </Space>
      </div>

      <Typography.Text type="secondary">
        {days[0].format('YYYY-MM-DD')} ~ {days[6].format('YYYY-MM-DD')}
      </Typography.Text>

      {loading || !schedule ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <div style={{ marginTop: 16 }}>
          <ConfirmedBanner
            confirmed={schedule.confirmed}
            onChangeClick={() => commonSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            onCancel={handleCancelConfirm}
          />

          <MemberTabs
            members={schedule.members}
            selectedMemberId={selectedMemberId}
            onChange={setSelectedMemberId}
          />

          <AvailabilityGrid
            days={days}
            times={times}
            selectedSlots={effectiveSlots}
            readOnly={!!selectedMember?.submitted}
            onToggle={handleToggle}
          />

          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {pendingCount > 0 && <Tag color="blue">변경사항 {pendingCount}건</Tag>}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={pendingCount === 0}
              onClick={handleSaveSlots}
            >
              저장
            </Button>

            {selectedMember?.submitted ? (
              <Button loading={submitting} onClick={() => handleSubmit(false)}>마감 취소</Button>
            ) : (
              <Button
                loading={submitting}
                disabled={pendingCount > 0 || effectiveSlots.size === 0}
                onClick={() => handleSubmit(true)}
              >
                마감하기
              </Button>
            )}
            {pendingCount > 0 && !selectedMember?.submitted && (
              <Typography.Text type="secondary">저장하지 않은 변경사항이 있어요. 먼저 저장해주세요</Typography.Text>
            )}
            {pendingCount === 0 && effectiveSlots.size === 0 && !selectedMember?.submitted && (
              <Typography.Text type="secondary">최소 1개 이상 선택해주세요</Typography.Text>
            )}
          </div>

          <div ref={commonSlotsRef}>
            <CommonSlotsPanel schedule={schedule} onConfirm={handleConfirm} />
          </div>
        </div>
      )}
    </div>
  );
}
